import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
// @ts-strict-ignore
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {Typography} from 'onboarding-components';

import {useTranslation} from 'common/i18n/hooks';
import {SvgIconReact} from 'common/components/svg-icon';
import {SvgIconType} from 'common/components/svg-icon/types';
import {TrackingEvent, trackOnboardingEvent} from 'iron/common/onboarding/immersive-onboarding/tracking';

import {mixpanelTheme} from './code-box-syntax-theme';
import style from './code-box.module.styl';

enum CopyState {
  DEFAULT = `default`,
  COPIED = `copied`,
  ERROR = `error`,
}

export const CodeBox: FC<{className?: string; code: string}> = ({className, code}) => {
  const {t} = useTranslation();

  const [copyState, setCopyState] = useState<CopyState>(CopyState.DEFAULT);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyClick = useCallback(() => {
    navigator.clipboard
      .writeText(code)
      .then(() => {
        trackOnboardingEvent(TrackingEvent.SnippetCopied);
        setCopyState(CopyState.COPIED);
      })
      .catch(() => {
        setCopyState(CopyState.ERROR);
      });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setCopyState(CopyState.DEFAULT);
    }, 2000);
  }, [code]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getCopyText = () => {
    switch (copyState) {
      case CopyState.COPIED:
        return t(`onboarding.install-js-sdk.copy-to-clipboard.copied`);
      case CopyState.ERROR:
        return t(`onboarding.install-js-sdk.copy-to-clipboard.copy-error`);
      default:
        return t(`onboarding.install-js-sdk.copy-to-clipboard.copy`);
    }
  };

  const getIcon = () => {
    switch (copyState) {
      case CopyState.COPIED:
        return `checkmark`;
      case CopyState.ERROR:
        return `x`;
      default:
        return `duplicate`;
    }
  };

  return (
    <div className={style.codeBoxContainer}>
      <button className={style.copyButton} onClick={handleCopyClick} aria-label={getCopyText()}>
        <Typography variant='captionXs' weight='bold'>
          {getCopyText()}
        </Typography>
        <SvgIconReact icon={getIcon() as SvgIconType} />
      </button>
      <SyntaxHighlighter className={className} language='javascript' style={mixpanelTheme}>
        {code}
      </SyntaxHighlighter>
    </div>
  );
};
